import { Heading } from 'Components/shared/Heading';
import { IS_ELECTRON } from 'Constants/env';
import {
  DEV_SETTINGS,
  COMMUNICATOR_HIDE_CHAT_CALL_HISTORY,
} from 'Constants/featureFlags';
import useFeatureFlags from 'Hooks/featureFlags/useFeatureFlags';
import { usePreferences } from 'Hooks/preferences/usePreferences'; // Import the hook
import { MobXProviderContext, observer } from 'mobx-react';
import { useSoftphoneDiagnosticsAccess } from 'Modules/softphone/hooks/useSoftphoneDiagnosticsAccess';
import React, { useCallback } from 'react';
import { RootStoreProps } from 'Stores/RootStore.types';
import { pushToGTMDataLayer } from 'Utils/analytics';
import { sendIpcSetOpenOnLogin } from 'Utils/ipcRendererEvents';
import { SwitchWithLabel } from '~/lib/ui/switchWithLabel/index';
import { Styled } from './index.styles';

export const GeneralSettings: React.FC = observer(() => {
  const {
    [DEV_SETTINGS]: devSettingsOn,
    [COMMUNICATOR_HIDE_CHAT_CALL_HISTORY]: hideChatCallHistory,
  } = useFeatureFlags<boolean>([
    DEV_SETTINGS,
    COMMUNICATOR_HIDE_CHAT_CALL_HISTORY,
  ]);

  const {
    pusher: { closeConnection, isOnline, openConnection },
  } = React.useContext<RootStoreProps>(MobXProviderContext);

  const testid = 'generalSettings';
  const {
    preferences,
    preferencesQuery,
    updatePreferences,
    desktopLocalPreferences,
    updateOpenOnLogin,
  } = usePreferences();

  const {
    isSettingVisible: isDiagnosticsSettingVisible,
    isDiagnosticsPanelEnabled,
    toggleDiagnosticsPanel,
  } = useSoftphoneDiagnosticsAccess();

  const handlePusherToggle = useCallback(() => {
    if (isOnline) {
      closeConnection();
    } else {
      openConnection();
    }
  }, [closeConnection, isOnline, openConnection]);

  if (preferencesQuery.isPending) return <div>Loading...</div>;
  if (preferencesQuery.isError) return <div>Error loading preferences</div>;

  const togglePreference = (
    preferenceName: keyof typeof preferences,
    value: boolean
  ) => {
    updatePreferences.mutate({
      [preferenceName]: value,
    });
  };

  const onToggleOpenOnLogin = (checked: boolean) => {
    void updateOpenOnLogin(checked);
    sendIpcSetOpenOnLogin(checked);
    pushToGTMDataLayer('toggleOpenOnLogin');
  };

  return (
    <div className="settings-general">
      <Heading variant="h2">General settings</Heading>
      <div className="semantic-flex-column semantic-flex-justify-space-between settings-general--wrapper">
        <div>
          {IS_ELECTRON && window['ipcRenderer'] !== undefined && (
            <div>
              <Heading variant="h3">Application</Heading>
              <div className="semantic-flex-column semantic-flex-justify-space-between">
                <span className="sub-headers margin-bottom-2rem">
                  Open on login
                </span>
                <SwitchWithLabel
                  id="openOnLogin"
                  label="Open Communicator when you log in to this computer (applies to desktop app only)"
                  checked={desktopLocalPreferences.openOnLogin}
                  onChange={onToggleOpenOnLogin}
                  testid={`${testid}-toggleButtonOpenOnLogin`}
                />
              </div>
            </div>
          )}

          {!hideChatCallHistory && (
            <Styled.SettingsOptions>
              <Heading variant="h3">Chat options</Heading>
              <div className="mt-5">
                <SwitchWithLabel
                  id="showCallMessagesInChat"
                  label="Enable call history in chat conversations"
                  checked={preferences.showCallMessagesInChat}
                  onChange={(checked) => togglePreference('showCallMessagesInChat', checked)}
                  helperText="Enabling this feature will display voice call events along with your conversations."
                  testid={`${testid}-toggleButtonCallHistoryInChatConversations`}
                />
              </div>
            </Styled.SettingsOptions>
          )}

          <Styled.SettingsOptions>
            <Heading variant="h3">Softphone</Heading>
            <div className="mt-5">
              <SwitchWithLabel
                id="floatingSoftphone"
                label="Fix softphone on the screen"
                checked={preferences.floatingSoftphone}
                onChange={(checked) => togglePreference('floatingSoftphone', checked)}
                testid={`${testid}-toggleButtonFixSoftPhoneOnScreen`}
              />
            </div>
            {/**
             * Softphone diagnostics panel access.
             * Visible only when the diagnostics feature flag is enabled.
             * Persisted per user.
             */}
            {isDiagnosticsSettingVisible && (
              <div className="mt-5">
                <SwitchWithLabel
                  id="toggleButtonSoftphoneDiagnostics"
                  label="Enable softphone diagnostics panel"
                  checked={isDiagnosticsPanelEnabled}
                  onChange={toggleDiagnosticsPanel}
                  helperText="When enabled, provides access to detailed diagnostic data to help investigate and debug call-related issues with the new softphone."
                  testid={`${testid}-toggleButtonSoftphoneDiagnostics`}
                />
              </div>
            )}
          </Styled.SettingsOptions>

          {devSettingsOn && (
            <Styled.SettingsOptions>
              <Heading variant="h3">Developer Options</Heading>
              <div className="mt-5">
                <SwitchWithLabel
                  id="togglePusher"
                  label="Is Pusher Enabled"
                  checked={isOnline}
                  onChange={handlePusherToggle}
                  testid={`${testid}-togglePusher`}
                />
              </div>
            </Styled.SettingsOptions>
          )}
        </div>
      </div>
    </div>
  );
});
